// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useState } from 'react';
import { Modal } from '@loa/ui-components';
import type { Client } from '../../../../types/client';

type MaxFundingScenarioPickerProps = Readonly<{
  open: boolean;
  clients: Client[];
  creating: boolean;
  onClose: () => void;
  onCreate: (clientId: string) => void;
}>;

export function MaxFundingScenarioPicker({
  open,
  clients,
  creating,
  onClose,
  onCreate,
}: MaxFundingScenarioPickerProps) {
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  useEffect(() => {
    if (open) {
      setSelectedClientId(clients[0]?.id ?? '');
    }
  }, [open, clients]);

  const handleCreate = () => {
    if (!selectedClientId || creating) return;
    onCreate(selectedClientId);
  };

  return (
    <Modal open={open} onClose={creating ? () => {} : onClose} size="medium">
      <Modal.Header title="New Scenario" onClose={creating ? undefined : onClose} />
      <Modal.Content>
        <p className="mb-md text-body-02 text-black font-sans">
          Who is this calculation for?
        </p>
        <div className="flex flex-col gap-md">
          {clients.map((client) => {
            const label = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.email;
            const isSelected = selectedClientId === client.id;
            return (
              <label
                key={client.id}
                className="flex items-center gap-[12px] cursor-pointer"
                data-testid={`scenario-picker-client-${client.id}`}
              >
                <input
                  type="radio"
                  name="scenario-client"
                  value={client.id}
                  checked={isSelected}
                  disabled={creating}
                  onChange={() => setSelectedClientId(client.id)}
                  className="sr-only"
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="7.5" fill="white" stroke="#2D0A6C" />
                  {isSelected && (
                    <circle cx="8" cy="8" r="3.5" fill="#2D0A6C" stroke="#2D0A6C" />
                  )}
                </svg>
                <span className="text-body-02 text-black font-sans">
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: creating }}
        primaryAction={{
          label: 'Create New Scenario',
          onClick: handleCreate,
          loading: creating,
          disabled: !selectedClientId || creating,
        }}
      />
    </Modal>
  );
}
